import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Leaf, Eye, EyeOff, ArrowRight, ArrowLeft, CheckCircle2, Sparkles, ShieldCheck, HeartPulse } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail || !password) return toast.error('Please fill all fields');
    setLoading(true);
    try {
      const user = await login(cleanEmail, password);
      toast.success('Welcome back, ' + user.name.split(' ')[0] + '!');
      navigate('/' + user.role + '/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 bg-white rounded-3xl shadow-2xl overflow-hidden border border-emerald-100/50">
        
        {/* Left Hero Sidebar */}
        <div className="bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 p-10 flex flex-col justify-between text-white relative overflow-hidden">
          
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl -ml-16 -mb-16 pointer-events-none" />

          <div className="relative z-10 space-y-6">
            
            {/* Header / Back Link */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20 shadow-sm">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl leading-tight">Avartana Care</h1>
                  <p className="text-emerald-200 text-xs font-medium">Ayurvedic Therapy Platform</p>
                </div>
              </div>

              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-100 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-xl border border-white/20 transition-all"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Overview</span>
              </Link>
            </div>

            {/* Title & Description */}
            <div className="pt-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-emerald-100 text-xs font-semibold mb-4">
                <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                <span>Holistic Healthcare & Healing</span>
              </div>
              <h2 className="text-3xl font-extrabold mb-3 leading-tight text-white">
                Ancient Wisdom,<br />Modern Care
              </h2>
              <p className="text-emerald-100 text-sm leading-relaxed">
                Seamless Ayurvedic therapy management — from initial diagnosis to long-term recovery, accessible for patients, doctors, and centers.
              </p>
            </div>

            {/* Platform Highlights (Replacing Demo Credentials) */}
            <div className="space-y-3 pt-2">
              <p className="text-emerald-200 text-xs font-bold uppercase tracking-wider">Why Avartana Care?</p>
              
              <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                <div className="p-1.5 rounded-lg bg-emerald-400/20 text-emerald-200 shrink-0 mt-0.5">
                  <HeartPulse className="w-4 h-4 text-emerald-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-xs text-white">Tailored Therapy Schedules</h4>
                  <p className="text-[11px] text-emerald-100/90 leading-tight">Panchakarma, Abhyanga & Shirodhara treatment plans.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                <div className="p-1.5 rounded-lg bg-teal-400/20 text-teal-200 shrink-0 mt-0.5">
                  <ShieldCheck className="w-4 h-4 text-teal-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-xs text-white">Certified Doctor Network</h4>
                  <p className="text-[11px] text-emerald-100/90 leading-tight">Direct consultation, digital prescriptions & progress charts.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                <div className="p-1.5 rounded-lg bg-cyan-400/20 text-cyan-200 shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-xs text-white">Inpatient Bed Management</h4>
                  <p className="text-[11px] text-emerald-100/90 leading-tight">Residential therapy suite allocation & 24/7 vitals monitoring.</p>
                </div>
              </div>
            </div>

          </div>

          {/* Footer note */}
          <div className="pt-6 text-[11px] text-emerald-200/80 border-t border-white/10 relative z-10 flex items-center justify-between">
            <span>© 2026 Avartana Care</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Secure Portal</span>
          </div>

        </div>

        {/* Right Sign In Form */}
        <div className="p-10 flex flex-col justify-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-1">Sign In</h2>
          <p className="text-gray-400 text-sm mb-8">Enter your credentials to access your portal</p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all shadow-lg shadow-emerald-200 hover:shadow-emerald-300 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-6">
            New patient? <Link to="/register" className="text-emerald-600 font-medium hover:underline">Create account</Link>
          </p>
        </div>

      </div>
    </div>
  );
}
